import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowLeft, ZoomIn, ZoomOut } from 'lucide-react';
import { Document, Page } from 'react-pdf';
import * as pdfjs from 'pdfjs-dist';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDFViewer = ({ pdfPath, projectTitle, onBack }) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);

  const onDocumentLoadSuccess = ({ numPages }) => {
    console.log(`PDF loaded successfully: ${pdfPath}, pages: ${numPages}`);
    setNumPages(numPages);
  };

  const onDocumentLoadError = (error) => {
    console.error(`PDF load error for ${pdfPath}:`, error);
  };

  const nextPage = () => {
    setCurrentPage(prev => prev < numPages ? prev + 1 : 1);
  };

  const prevPage = () => {
    setCurrentPage(prev => prev > 1 ? prev - 1 : numPages);
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  // Watermark component
  const Watermark = () => (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        {Array.from({ length: 30 }, (_, i) => (
          <div
            key={i}
            className="absolute text-purple-600 font-bold text-xl transform -rotate-45"
            style={{
              left: `${(i * 12) % 100}%`,
              top: `${(i * 8) % 100}%`,
              animation: `watermarkFloat ${4 + (i % 4)}s ease-in-out infinite`
            }}
          >
            HyperHumane
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center space-x-2 text-purple-600 hover:text-purple-800 transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Back to Projects</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-semibold text-primary-900">{projectTitle}</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={zoomOut}
                  className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                  disabled={scale <= 0.5}
                >
                  <ZoomOut size={20} />
                </button>
                <span className="text-sm text-purple-700 min-w-[60px] text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={zoomIn}
                  className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                  disabled={scale >= 3}
                >
                  <ZoomIn size={20} />
                </button>
              </div>
              
              {numPages && (
                <div className="text-sm text-purple-800 font-medium">
                  Page {currentPage} of {numPages}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center">
          <div className="relative bg-white rounded-lg shadow-lg p-8">
            <Document 
              file={pdfPath} 
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={<div className="text-purple-600">Loading PDF...</div>}
              error={<div className="text-red-600">Failed to load PDF</div>}
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
            <Watermark />
            
            {/* Navigation arrows */}
            {numPages > 1 && (
              <>
                <button
                  onClick={prevPage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-purple-100 hover:bg-purple-200 text-purple-800 p-3 rounded-full shadow-lg transition-all duration-200 z-10"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={nextPage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-purple-100 hover:bg-purple-200 text-purple-800 p-3 rounded-full shadow-lg transition-all duration-200 z-10"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Page navigation */}
        {numPages > 1 && (
          <div className="flex justify-center mt-8">
            <div className="flex items-center space-x-4">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              
              <div className="flex space-x-2">
                {Array.from(new Array(numPages), (_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`w-3 h-3 rounded-full transition-all duration-200 ${
                      currentPage === index + 1 ? 'bg-purple-600' : 'bg-purple-300 hover:bg-purple-400'
                    }`}
                  />
                ))}
              </div>
              
              <button
                onClick={nextPage}
                disabled={currentPage === numPages}
                className="px-4 py-2 bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Watermark animation CSS */}
      <style jsx>{`
        @keyframes watermarkFloat {
          0%, 100% { opacity: 0.1; transform: translateY(0px) rotate(-45deg); }
          50% { opacity: 0.2; transform: translateY(-3px) rotate(-45deg); }
        }
      `}</style>
    </div>
  );
};

export default PDFViewer; 